import { StyleSheet, ViewProps } from 'react-native'
import { InsightTrendId, useBuilding, useCompany, useInsightsTrend, useStore } from 'zens-store'
import {
  CalendarValue,
  Spacing,
  displayTitlesForCalendarValue,
  calendarValueToInsightsFromTo,
  Weekday,
  titleForWeekday,
} from 'zens-storybook'
import * as XLSX from 'xlsx'
import { useTranslation } from 'react-i18next'
import generatePDF from 'react-to-pdf'

import {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  ReactElement,
  RefAttributes,
  useImperativeHandle,
} from 'react'

import InsightsTrendGraph from './trendgraph'

import { GraphBar } from '.'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: Spacing.M,
  },
})

export interface InsightsTrendRef {
  download: (key: string) => Promise<void>
}

interface Props extends ViewProps {
  companyId: number
  buildingId: number
  trendId?: InsightTrendId
  period: CalendarValue
  weekdays?: Weekday[]
  workingHours?: boolean
  bars?: GraphBar[]
  onChangeBars?: (bars: GraphBar[]) => void
}

const InsightsTrend = forwardRef<InsightsTrendRef, Props>((props, ref) => {
  const { companyId, buildingId, trendId, period, weekdays, workingHours, bars, onChangeBars, style, ...otherProps } =
    props
  const { i18n, message } = useStore()
  const { t } = useTranslation()

  const dates = calendarValueToInsightsFromTo(period, weekdays)
  const insights = useInsightsTrend(
    {
      companyId,
      buildingId,
      trendId: trendId || 'floor',
      from: dates?.from || new Date(),
      to: dates?.to || new Date(),
      weekdays,
      workingHours,
    },
    { enabled: !!dates },
  )
  const company = useCompany({ companyId })
  const building = useBuilding({ companyId, buildingId })

  useImperativeHandle(ref, () => ({
    download: (key) => download(key as 'data' | 'graph'),
  }))

  function titleForType(type?: string) {
    switch (type) {
      case 'floor':
        return `${t('Trends')} - ${t('By floor')}`
      case 'zone':
        return `${t('Trends')} - ${t('By zone')}`
      case 'workspacetype':
        return `${t('Trends')} - ${t('By workspace type')}`
      case 'weekday':
        return `${t('Trends')} - ${t('By weekday')}`
      default:
        return ''
    }
  }

  function columnForType(type?: string) {
    switch (type) {
      case 'floor':
        return t('Floor')
      case 'zone':
        return t('Zone')
      case 'workspacetype':
        return t('Workspace type')
      case 'weekday':
        return t('Weekday')
      default:
        return ''
    }
  }

  async function download(key: 'data' | 'graph') {
    if (key === 'data') {
      try {
        const periodTitles = displayTitlesForCalendarValue(period, i18n.language)
        const rows: (string | number)[][] = []
        rows.push([t('Company'), company.data?.name || '-'])
        rows.push([t('Building'), building.data?.name || '-'])
        rows.push([t('From'), periodTitles[0] || '-'])
        rows.push([t('To'), periodTitles[1] || '-'])
        rows.push([t('Weekdays'), ...(weekdays?.map((weekday) => titleForWeekday(weekday, t)) || [t('All days')])])
        rows.push([t('Working hours'), workingHours ? t('Yes') : t('No')])
        rows.push([])
        rows.push([
          columnForType(trendId),
          ...(!bars || bars.includes('average') ? [t('Average occupancy')] : []),
          ...(!bars || bars.includes('peak') ? [t('Peak occupancy')] : []),
        ])

        insights.data?.forEach((item) =>
          rows.push([
            trendId === 'weekday' ? titleForWeekday(item.name as Weekday, t) : item.name,
            ...(!bars || bars.includes('average') ? [item.value ?? 0] : []),
            ...(!bars || bars.includes('peak') ? [item.peak ?? 0] : []),
          ]),
        )
        const worksheet = XLSX.utils.aoa_to_sheet(rows)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Insights')
        XLSX.writeFile(workbook, `${titleForType(trendId)}.xlsx`, { compression: true })
      } catch (error) {
        message.add({ message: t('Error generating data'), status: 'error' })
        console.error('Error generating data', error)
      }
    } else {
      try {
        await generatePDF(() => document.getElementById('InsightsTrendGraph'), {
          filename: `${titleForType(trendId)}.pdf`,
        })
      } catch (error) {
        message.add({ message: t('Error generating graph'), status: 'error' })
        console.error('Error generating graph', error)
      }
    }
  }

  return (
    <InsightsTrendGraph
      companyId={companyId}
      buildingId={buildingId}
      trendId={trendId as InsightTrendId}
      insights={insights.data}
      bars={bars}
      onChangeBars={onChangeBars}
      style={[styles.container, style]}
      {...otherProps}
    />
  )
}) as ForwardRefExoticComponent<PropsWithoutRef<Props> & RefAttributes<InsightsTrendRef>> & {
  Skeleton: (props: SkeletonProps) => ReactElement
}

interface SkeletonProps extends ViewProps {
  trendId?: InsightTrendId
}

InsightsTrend.Skeleton = (props: SkeletonProps) => (
  <InsightsTrendGraph.Skeleton style={styles.container} trendId={props.trendId} />
)

export default InsightsTrend
